import { useState } from "react";
import { Loader2, ShieldCheck, KeyRound, Copy, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/** Six-digit code step shown after the password is accepted. */
export function TwoFactorStep({ mfaToken, onDone }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login/2fa", { mfa_token: mfaToken, code });
      onDone(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-8 space-y-4" data-testid="twofa-form">
      <div className="flex items-center gap-2 text-primary">
        <ShieldCheck className="h-5 w-5" />
        <p className="text-sm font-medium">Two-step verification</p>
      </div>
      <p className="text-sm text-muted-foreground">
        Enter the 6-digit code from your authenticator app. You can also use one of your recovery codes.
      </p>
      <div className="space-y-2">
        <Label htmlFor="code">Code</Label>
        <Input id="code" data-testid="twofa-code" value={code} autoFocus autoComplete="one-time-code"
          onChange={(e) => setCode(e.target.value)} placeholder="123456" required />
      </div>
      {error && <p data-testid="twofa-error" className="text-sm text-destructive">{error}</p>}
      <Button type="submit" className="w-full" disabled={busy} data-testid="twofa-submit">
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Verify
      </Button>
    </form>
  );
}

/** Forced enrolment: QR, verify, then the recovery codes shown exactly once. */
export function TwoFactorEnroll({ mfaToken, onDone }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [codes, setCodes] = useState(null);
  const [pending, setPending] = useState(null);
  const [copied, setCopied] = useState(false);

  const start = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/2fa/start", { mfa_token: mfaToken });
      setSetup(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/2fa/verify", { mfa_token: mfaToken, code });
      setCodes(data.recovery_codes);
      setPending(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (codes) {
    return (
      <div className="mt-8 space-y-4" data-testid="recovery-codes">
        <div className="flex items-center gap-2 text-primary">
          <KeyRound className="h-5 w-5" />
          <p className="text-sm font-medium">Save your recovery codes</p>
        </div>
        <p className="text-sm text-muted-foreground">
          These are shown once and each works once. Keep them somewhere you can reach without your phone —
          they are the only way back in if you lose it.
        </p>
        <div className="grid grid-cols-2 gap-2 rounded-md border border-border bg-muted/40 p-4 font-mono text-sm">
          {codes.map((c) => <span key={c}>{c}</span>)}
        </div>
        <Button variant="outline" className="w-full" data-testid="copy-recovery-codes"
          onClick={() => { navigator.clipboard.writeText(codes.join("\n")); setCopied(true); }}>
          {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
          {copied ? "Copied" : "Copy all"}
        </Button>
        <Button className="w-full" data-testid="recovery-done" onClick={() => onDone(pending)}>
          I've saved them — continue
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-8 space-y-4" data-testid="enroll-form">
      <div className="flex items-center gap-2 text-primary">
        <ShieldCheck className="h-5 w-5" />
        <p className="text-sm font-medium">Set up two-step verification</p>
      </div>
      <p className="text-sm text-muted-foreground">
        This account can publish to live client sites, so two-step verification is required. Scan the code with
        Google Authenticator, 1Password, Authy or any TOTP app.
      </p>
      {!setup ? (
        <Button className="w-full" onClick={start} disabled={busy} data-testid="enroll-start">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Show my QR code
        </Button>
      ) : (
        <form onSubmit={verify} className="space-y-4">
          <img src={setup.qr} alt="Authenticator QR code" className="mx-auto rounded-md border border-border bg-white p-2"
            width={200} height={200} data-testid="enroll-qr" />
          <p className="text-center text-xs text-muted-foreground">
            Can't scan it? Enter this key by hand:<br />
            <span className="font-mono text-foreground" data-testid="enroll-secret">{setup.secret}</span>
          </p>
          <div className="space-y-2">
            <Label htmlFor="enrollcode">Code from your app</Label>
            <Input id="enrollcode" data-testid="enroll-code" value={code} autoFocus autoComplete="one-time-code"
              onChange={(e) => setCode(e.target.value)} placeholder="123456" required />
          </div>
          {error && <p data-testid="enroll-error" className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" disabled={busy} data-testid="enroll-verify">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Turn on two-step verification
          </Button>
        </form>
      )}
      {!setup && error && <p data-testid="enroll-error" className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
